import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../base/ui/components/web/ContextMenuItemGroup';
import { setGifMenuVisibility } from '../../../gifs/actions';
import { isGifsMenuOpen } from '../../../gifs/functions.web';
import { DRAWER_MAX_HEIGHT } from '../../constants';
import { showOverflowDrawer } from '../../functions.web';
import Drawer from './Drawer';
import JitsiPortal from './JitsiPortal';
import { setHangupMenuVisible, setOverflowMenuVisible, setToolbarHovered } from "../../actions.web";
import OverflowToggleButtonMobile from "./OverflowToggleButtonMobile";
import ChatCounterMobile from "../../../chat/components/web/ChatCounterMobile";
const useStyles = makeStyles()((_theme, { reactionsMenuHeight, overflowDrawer }) => {
    return {
        mt12: {
            marginTop: "12px",
        },
        overflowMenuDrawer: {
            overflowY: 'scroll',
            maxHeight: `calc(${DRAWER_MAX_HEIGHT})`
        },
        contextMenu: {
            position: 'relative',
            right: 'auto',
            margin: 0,
            marginBottom: '10px',
            maxHeight: overflowDrawer ? undefined : [
                'calc(100vh - 100px)',
                'calc(100dvh - 100px)',
            ],
            minWidth: '298px',
            overflow: 'hidden'
        },
        separateLineContainer: {
            paddingTop: '6px',
            paddingBottom: '7px',
            paddingLeft: '24px',
            paddingRight: '24px'
        },
        separateLine: {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            height: '1px'
        },
        content: {
            position: 'relative',
            maxHeight: [
                overflowDrawer
                    ? `calc(100% - ${reactionsMenuHeight}px - 16px)` : `calc(100vh - 100px - ${reactionsMenuHeight}px)`,
                overflowDrawer
                    ? `calc(100% - ${reactionsMenuHeight}px - 16px)` : `calc(100dvh - 100px - ${reactionsMenuHeight}px)`,
            ],
            overflowY: 'auto'
        },
        footerSeparatorContainer: {
            padding: '0px 18px',
        },
        footerSeparator: {
            borderTop: '1px dashed rgba(255, 255, 255, 0.1)',
        },
        footer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            '&.is-mobile': {
                position: 'relative',
            }
        },
        reactionsPadding: {
            height: `${reactionsMenuHeight}px`
        }
    };
});
const OverflowMenuButtonMobile = ({ buttons, headerContent }) => {
    const overflowDrawer = useSelector(showOverflowDrawer);
    const isGiphyVisible = useSelector(isGifsMenuOpen);
    const dispatch = useDispatch();
    const isOpen = useSelector((state) => state['features/toolbox'].overflowMenuVisible);
    const hangupMenuVisible = useSelector((state) => state['features/toolbox'].hangupMenuVisible);
    /**
     * Sets the visibility of the overflow menu.
     *
     * @param {boolean} visible - Whether or not the overflow menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onVisibilityChange = useCallback((visible) => {
        dispatch(setOverflowMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    const onCloseDialog = useCallback(() => {
        onVisibilityChange(false);
        if (isGiphyVisible && !overflowDrawer) {
            dispatch(setGifMenuVisibility(false));
        }
    }, [onVisibilityChange, setGifMenuVisibility, isGiphyVisible, overflowDrawer, dispatch]);
    const onEscClick = useCallback((event) => {
        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            event.stopPropagation();
            onCloseDialog();
        }
    }, [onCloseDialog]);
    const toggleDialogVisibility = useCallback(() => {
        sendAnalytics(createToolbarEvent('overflow'));
        onVisibilityChange(!isOpen);
    }, [isOpen, onVisibilityChange]);
    const toolbarAccLabel = 'toolbar.accessibilityLabel.moreActionsMenu';
    const { t } = useTranslation();
    let reactionsMenuHeight = 0;
    const { classes, cx } = useStyles({
        reactionsMenuHeight,
        overflowDrawer
    });
    /**
     * Key handler for overflow/hangup menus.
     *
     * @param {KeyboardEvent} e - Esc key click to close the popup.
     * @returns {void}
     */
    const onToolboxEscKey = useCallback((e) => {
        if (e?.key === 'Escape') {
            e?.stopPropagation();
            hangupMenuVisible && dispatch(setHangupMenuVisible(false));
            isOpen && dispatch(setOverflowMenuVisible(false));
        }
    }, [dispatch, hangupMenuVisible, isOpen]);
    const groupsJSX = (React.createElement(React.Fragment, null,
        headerContent,
        React.createElement(ContextMenuItemGroup, { className: classes.mt12, key: `group-0` }, buttons.map((buttonGroup) => (buttonGroup
            .map(({ key, Content, ...rest }) => {
            const props = { ...rest };
            if (key !== 'reactions') {
                props.buttonKey = key;
                props.contextMenu = true;
                props.showLabel = true;
            }
            return (React.createElement(Content, { ...props, key: key }));
        }))))));
    const overflowMenu = groupsJSX && (React.createElement(ContextMenu, { accessibilityLabel: t(toolbarAccLabel), className: classes.contextMenu, hidden: false, id: 'overflow-context-menu', inDrawer: overflowDrawer, onKeyDown: onToolboxEscKey },
        React.createElement("div", { className: classes.content }, groupsJSX)));
    return (React.createElement("div", { className: 'toolbox-button-wth-dialog context-menu' },
        React.createElement(React.Fragment, null,
            React.createElement("div", { style: {
                    position: "relative",
                } },
                React.createElement(OverflowToggleButtonMobile, { handleClick: toggleDialogVisibility, isOpen: isOpen, onKeyDown: onEscClick, customClass: 'mobile-header-button' }),
                React.createElement(ChatCounterMobile, { customClass: "overflow-button" })),
            React.createElement(JitsiPortal, null,
                React.createElement(Drawer, { isOpen: isOpen, onClose: onCloseDialog, position: "top" },
                    React.createElement(React.Fragment, null,
                        React.createElement("div", { className: classes.overflowMenuDrawer }, overflowMenu)))))));
};
export default OverflowMenuButtonMobile;
